import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent as ReactFocusEvent,
} from "react";

const MOBILE_QUERY = "(max-width: 980px)";
const HOVER_QUERY = "(hover: hover) and (pointer: fine)";
const COLLAPSE_DELAY_MS = 1_700;
const UNPIN_DELAY_MS = 1_800;

function matchesMobileViewport(): boolean {
  return typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches;
}

export function useBackstageSidebar() {
  const [isMobile, setIsMobile] = useState(matchesMobileViewport);
  const [collapsed, setCollapsed] = useState(true);
  const [pinned, setPinned] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const sidebarRef = useRef<HTMLElement>(null);
  const edgeToggleRef = useRef<HTMLButtonElement>(null);
  const mobileCloseRef = useRef<HTMLButtonElement>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);
  const collapseTimerRef = useRef<number | null>(null);
  const pointerInsideRef = useRef(false);
  const isMobileRef = useRef(isMobile);
  const pinnedRef = useRef(pinned);
  const mobileOpenRef = useRef(mobileOpen);

  useEffect(() => {
    isMobileRef.current = isMobile;
  }, [isMobile]);

  useEffect(() => {
    pinnedRef.current = pinned;
  }, [pinned]);

  useEffect(() => {
    mobileOpenRef.current = mobileOpen;
  }, [mobileOpen]);

  const clearCollapseTimer = useCallback(() => {
    if (collapseTimerRef.current === null) return;
    window.clearTimeout(collapseTimerRef.current);
    collapseTimerRef.current = null;
  }, []);

  const scheduleCollapse = useCallback(
    (delay = COLLAPSE_DELAY_MS) => {
      clearCollapseTimer();
      if (isMobileRef.current || pinnedRef.current) return;

      collapseTimerRef.current = window.setTimeout(() => {
        const sidebar = sidebarRef.current;
        const pointerInside = pointerInsideRef.current || Boolean(sidebar?.matches(":hover"));
        const focusInside = Boolean(
          sidebar && document.activeElement && sidebar.contains(document.activeElement),
        );
        if (!isMobileRef.current && !pinnedRef.current && !pointerInside && !focusInside) {
          setCollapsed(true);
        }
        collapseTimerRef.current = null;
      }, delay);
    },
    [clearCollapseTimer],
  );

  const showDesktopPreview = useCallback(() => {
    clearCollapseTimer();
    if (isMobileRef.current || pinnedRef.current) return;
    setCollapsed(false);
  }, [clearCollapseTimer]);

  const rememberTrigger = useCallback(() => {
    lastTriggerRef.current =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
  }, []);

  const openBackstage = useCallback(() => {
    rememberTrigger();
    clearCollapseTimer();
    if (isMobileRef.current) {
      mobileOpenRef.current = true;
      setMobileOpen(true);
      window.requestAnimationFrame(() => mobileCloseRef.current?.focus());
      return;
    }
    setCollapsed(false);
    window.requestAnimationFrame(() => edgeToggleRef.current?.focus());
  }, [clearCollapseTimer, rememberTrigger]);

  const closeMobile = useCallback((returnFocus = true) => {
    if (!isMobileRef.current || !mobileOpenRef.current) return;
    if (returnFocus) lastTriggerRef.current?.focus();
    mobileOpenRef.current = false;
    setMobileOpen(false);
  }, []);

  const restoreDesktop = useCallback(() => {
    clearCollapseTimer();
    setCollapsed(false);
    window.requestAnimationFrame(() => edgeToggleRef.current?.focus());
  }, [clearCollapseTimer]);

  const togglePinned = useCallback(() => {
    if (isMobileRef.current) return;
    clearCollapseTimer();
    if (pinnedRef.current) {
      pinnedRef.current = false;
      setPinned(false);
      setCollapsed(false);
      scheduleCollapse(UNPIN_DELAY_MS);
      return;
    }
    pinnedRef.current = true;
    setPinned(true);
    setCollapsed(false);
  }, [clearCollapseTimer, scheduleCollapse]);

  const handlePointerEnter = useCallback(() => {
    pointerInsideRef.current = true;
    if (window.matchMedia(HOVER_QUERY).matches) showDesktopPreview();
  }, [showDesktopPreview]);

  const handlePointerLeave = useCallback(() => {
    pointerInsideRef.current = false;
    scheduleCollapse(1_500);
  }, [scheduleCollapse]);

  const handleFocusCapture = useCallback(
    (event: ReactFocusEvent<HTMLElement>) => {
      const target = event.target as HTMLElement;
      if (!target.hasAttribute("data-sidebar-restore")) showDesktopPreview();
    },
    [showDesktopPreview],
  );

  const handleBlurCapture = useCallback(() => {
    window.setTimeout(() => scheduleCollapse(1_500), 0);
  }, [scheduleCollapse]);

  useEffect(() => {
    const media = window.matchMedia(MOBILE_QUERY);
    const reconcileBreakpoint = (event: MediaQueryListEvent) => {
      clearCollapseTimer();
      const nextIsMobile = event.matches;
      const focusWasInside = Boolean(
        sidebarRef.current &&
          document.activeElement &&
          sidebarRef.current.contains(document.activeElement),
      );

      if (nextIsMobile && focusWasInside) lastTriggerRef.current?.focus();
      isMobileRef.current = nextIsMobile;
      setIsMobile(nextIsMobile);
      setMobileOpen(false);
      pointerInsideRef.current = false;

      if (!nextIsMobile && focusWasInside) {
        window.requestAnimationFrame(() => edgeToggleRef.current?.focus());
      }
    };

    media.addEventListener("change", reconcileBreakpoint);
    return () => {
      media.removeEventListener("change", reconcileBreakpoint);
      clearCollapseTimer();
    };
  }, [clearCollapseTimer]);

  useEffect(() => {
    const trapMobileFocus = (event: KeyboardEvent) => {
      if (!isMobileRef.current || !mobileOpenRef.current) return;
      if (event.key === "Escape") {
        event.preventDefault();
        closeMobile(true);
        return;
      }
      if (event.key !== "Tab" || !sidebarRef.current) return;

      const focusable = Array.from(
        sidebarRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter(
        (element) =>
          element.offsetParent !== null &&
          window.getComputedStyle(element).visibility !== "hidden",
      );
      const first = focusable[0];
      const last = focusable.at(-1);
      if (!first || !last) return;

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", trapMobileFocus);
    return () => document.removeEventListener("keydown", trapMobileFocus);
  }, [closeMobile]);

  return {
    collapsed,
    pinned,
    isMobile,
    mobileOpen,
    sidebarRef,
    edgeToggleRef,
    mobileCloseRef,
    openBackstage,
    closeMobile,
    restoreDesktop,
    togglePinned,
    handlePointerEnter,
    handlePointerLeave,
    handleFocusCapture,
    handleBlurCapture,
  };
}
